import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany, PageHeader, EmptyState, Money, StatusBadge, LineItemsEditor, computeTotals } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, FileCheck2 } from "lucide-react";

export default function Quotes() {
  const { companyId, cur } = useCompany();
  const [items, setItems] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ customer_id: "", lines: [{ product_id: "", name: "", qty: 1, unit_price: 0, tax_rate: 0 }] });

  const load = () => api.get("/quotes", { params: { company_id: companyId } }).then(({ data }) => setItems(data.quotes));
  useEffect(() => {
    if (!companyId) return;
    load();
    api.get("/customers", { params: { company_id: companyId } }).then(({ data }) => setCustomers(data.customers));
    api.get("/products", { params: { company_id: companyId } }).then(({ data }) => setProducts(data.products));
  }, [companyId]);

  const totals = computeTotals(form.lines);
  const create = async () => {
    if (!form.customer_id) return toast.error("Choisissez un client");
    setSaving(true);
    try { await api.post("/quotes", form, { params: { company_id: companyId } }); toast.success("Devis créé");
      setOpen(false); setForm({ customer_id: "", lines: [{ product_id: "", name: "", qty: 1, unit_price: 0, tax_rate: 0 }] }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const convert = async (id) => {
    try { await api.post(`/quotes/${id}/convert`, {}, { params: { company_id: companyId } }); toast.success("Devis converti en facture"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="quotes-page">
      <PageHeader title="Devis" subtitle="Proposez et convertissez vos devis en factures." action={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-quote-button"><Plus className="w-4 h-4 mr-1" /> Nouveau devis</Button></DialogTrigger>
          <DialogContent className="bg-popover max-w-3xl">
            <DialogHeader><DialogTitle className="font-display">Nouveau devis</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1"><Label>Client *</Label>
                <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="quote-customer-select">
                  <option value="">— Choisir —</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <LineItemsEditor lines={form.lines} setLines={(l) => setForm({ ...form, lines: l })} products={products} />
              <div className="flex justify-end gap-6 text-sm border-t border-border pt-3">
                <span className="font-display font-bold">Total <Money value={totals.total} cur={cur} /></span>
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} className="rounded-full" data-testid="save-quote-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer le devis"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>} />
      <Card className="rounded-md border-border shadow-sm overflow-hidden">
        {items === null ? <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState title="Aucun devis" />
          : <Table>
              <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{items.map((q) => (
                <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                  <TableCell className="font-mono text-xs">{q.number}</TableCell>
                  <TableCell className="font-medium">{q.customer_name}</TableCell>
                  <TableCell className="text-right"><Money value={q.total} cur={cur} /></TableCell>
                  <TableCell><StatusBadge status={q.status} /></TableCell>
                  <TableCell className="text-right">{q.status !== "accepted"
                    ? <Button size="sm" variant="outline" className="rounded-full" onClick={() => convert(q.id)} data-testid={`convert-quote-${q.id}`}><FileCheck2 className="w-3.5 h-3.5 mr-1" />Convertir</Button>
                    : <span className="text-xs text-emerald-600">Facturé</span>}</TableCell>
                </TableRow>))}
              </TableBody>
            </Table>}
      </Card>
    </div>
  );
}
